import { Outlet } from 'react-router-dom';
import { useStore } from '@lib/storeContext';
import { useCart } from '@lib/cartContext';
import StoreHeader from '../../app/StoreHeader';
import OnboardingWrapper from '../../app/OnboardingWrapper';
import Link from 'next/link';

/**
 * Main marketplace layout for Vite.
 * Mirrors the Next.js StoreLayoutWrapper but runs entirely client-side.
 * Renders announcement bar, header, main content, and footer.
 */
export default function MarketplaceLayout() {
  const storeSettings = useStore();
  const { } = useCart(); // ensure cart provider is active

  return (
    <OnboardingWrapper>
      <div className="min-h-screen bg-[#f9fafb] text-slate-900">
        {/* Announcement Bar */}
        {storeSettings.announcementBar && (
          <div
            className="text-white text-center py-2 px-4 text-sm font-medium"
            style={{ backgroundColor: storeSettings.themeColor }}
          >
            {storeSettings.announcementBar}
          </div>
        )}

        {/* Header */}
        <StoreHeader storeSettings={storeSettings} />

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 py-8">
          <Outlet />
        </main>

        {/* Footer */}
        <footer className="bg-white border-t border-slate-200 mt-16">
          <div className="max-w-7xl mx-auto px-4 py-12">
            <div className="grid md:grid-cols-3 gap-8">
              <div>
                <h3 className="font-bold text-lg text-slate-900 mb-4">
                  {storeSettings.displayName}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Fresh, quality produce delivered directly to your door.
                  Supporting local farms and sustainable agriculture.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 mb-4">Quick Links</h4>
                <ul className="space-y-2 text-sm">
                  <li>
                    <Link href="/" className="text-slate-600 hover:text-slate-900">
                      Home
                    </Link>
                  </li>
                  <li>
                    <Link href="/catalog" className="text-slate-600 hover:text-slate-900">
                      Shop All
                    </Link>
                  </li>
                  <li>
                    <Link href="/cart" className="text-slate-600 hover:text-slate-900">
                      Cart
                    </Link>
                  </li>
                  {storeSettings.showReturns && (
                    <li>
                      <Link href="/returns" className="text-slate-600 hover:text-slate-900">
                        Returns
                      </Link>
                    </li>
                  )}
                  {storeSettings.showSupport && (
                    <li>
                      <Link href="/support" className="text-slate-600 hover:text-slate-900">
                        Support
                      </Link>
                    </li>
                  )}
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 mb-4">Contact</h4>
                <ul className="space-y-2 text-sm text-slate-600">
                  {storeSettings.contactEmail && (
                    <li>
                      <a
                        href={`mailto:${storeSettings.contactEmail}`}
                        className="hover:text-slate-900"
                      >
                        {storeSettings.contactEmail}
                      </a>
                    </li>
                  )}
                  {storeSettings.contactPhone && (
                    <li>
                      <a
                        href={`tel:${storeSettings.contactPhone}`}
                        className="hover:text-slate-900"
                      >
                        {storeSettings.contactPhone}
                      </a>
                    </li>
                  )}
                </ul>
              </div>
            </div>
            <div className="mt-8 pt-8 border-t border-slate-200 text-center">
              <div className="flex flex-col items-center gap-4">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <span>Powered by</span>
                  <img
                    src="https://www.saletrax.net/logo.png"
                    alt="Saletrax"
                    className="h-6 w-auto"
                  />
                  <span>&bull; &copy; {new Date().getFullYear()}</span>
                </div>
                <p className="text-xs text-slate-500">
                  To get your own website like this and portal, email{' '}
                  <a
                    href="mailto:eli@saletrax.net"
                    className="text-slate-700 hover:text-slate-900 underline"
                  >
                    eli@saletrax.net
                  </a>
                  {' '}or call{' '}
                  <a
                    href="tel:9292165491"
                    className="text-slate-700 hover:text-slate-900 underline"
                  >
                    929-216-5491
                  </a>
                </p>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </OnboardingWrapper>
  );
}
